<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    right
    fixed
    temporary
    width="360"
    class="journal-filters-drawer"
  >
    <div class="jfd-header">
      <span class="jfd-title">{{ labels.filterBy }}</span>
      <button class="jfd-close-btn" type="button" @click="close">
        <v-icon small>fa-regular fa-xmark</v-icon>
      </button>
    </div>

    <div class="jfd-content">
      <v-expansion-panels v-model="expandedPanel" accordion flat>
        <v-expansion-panel>
          <v-expansion-panel-header class="jfd-panel-header">
            {{ labels.time }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="jfd-period-list">
              <button
                v-for="option in periodOptions"
                :key="option.value"
                type="button"
                class="jfd-period-option"
                :class="{ active: localState.month === option.value }"
                @click="selectPeriod(option.value)"
              >
                <span
                  class="jfd-period-marker"
                  :class="{ checked: localState.month === option.value }"
                />
                <span class="jfd-period-label">{{ option.label }}</span>
              </button>
            </div>
            <div v-if="isCustomPeriod" class="jfd-custom-dates mt-3">
              <v-text-field
                v-model="dateRangeText"
                :label="labels.selectedPeriod"
                readonly
                disabled
                dense
                outlined
                hide-details
              />
              <v-date-picker
                v-model="localDates"
                range
                class="jfd-date-picker mt-2"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-for="section in checkboxSections" :key="section.key">
          <v-expansion-panel-header class="jfd-panel-header">
            {{ labels[section.labelKey] }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="jfd-checkbox-list">
              <button
                type="button"
                class="jfd-period-option"
                @click="toggleSelectAll(section.key, section.options)"
              >
                <span
                  class="jfd-period-marker"
                  :class="{ checked: isAllSelected(section.key, section.options) }"
                />
                <span class="jfd-period-label">{{ labels.selectAll }}</span>
              </button>
              <button
                v-for="(option, index) in section.options"
                :key="`${section.key}-${option.value}-${index}`"
                type="button"
                class="jfd-period-option"
                @click="toggleOption(section.key, option.value, section.options)"
              >
                <span
                  class="jfd-period-marker"
                  :class="{ checked: isOptionSelected(section.key, option.value, section.options) }"
                />
                <span class="jfd-period-label">{{ option.label }}</span>
              </button>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <div class="jfd-footer">
      <div class="jfd-footer-actions">
        <v-btn
          v-if="showClear"
          class="jfd-clear-btn secondary"
          rounded
          @click="clearAll"
        >
          {{ labels.clearFilters }}
          <v-icon small class="ml-2">fa-regular fa-rotate-left</v-icon>
        </v-btn>
        <v-btn
          class="jfd-apply-btn"
          :class="{ 'jfd-apply-btn--compact': showClear }"
          :block="!showClear"
          rounded
          @click="apply"
        >
          {{ labels.applyFilter }}
          <v-icon small class="ml-2">fa-regular fa-check</v-icon>
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "JournalFiltersDrawer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    filterState: {
      type: Object,
      required: true,
    },
    defaultFilterState: {
      type: Object,
      required: true,
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      required: true,
    },
    periodOptions: {
      type: Array,
      required: true,
    },
    accountOptions: {
      type: Array,
      default: () => [],
    },
    assetOptions: {
      type: Array,
      default: () => [],
    },
    sideOptions: {
      type: Array,
      default: () => [],
    },
    resultOptions: {
      type: Array,
      default: () => [],
    },
    moodOptions: {
      type: Array,
      default: () => [],
    },
    strategyOptions: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localState: this.cloneState(this.filterState),
      localDates: [...this.dates],
      expandedPanel: null,
    };
  },
  computed: {
    isCustomPeriod() {
      return this.localState.month === "customPeriod";
    },
    dateRangeText() {
      return this.localDates.join(" ~ ");
    },
    checkboxSections() {
      return [
        {
          key: "accounts",
          labelKey: "accounts",
          options: this.accountOptions,
          multi: true,
        },
        {
          key: "assets",
          labelKey: "assets",
          options: this.assetOptions,
          multi: true,
        },
        {
          key: "sides",
          labelKey: "sides",
          options: this.sideOptions,
          multi: false,
        },
        {
          key: "results",
          labelKey: "results",
          options: this.resultOptions,
          multi: false,
        },
        {
          key: "moods",
          labelKey: "moods",
          options: this.moodOptions,
          multi: true,
        },
        {
          key: "strategies",
          labelKey: "strategies",
          options: this.strategyOptions,
          multi: true,
        },
      ];
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.syncFromProps();
      }
    },
    filterState: {
      deep: true,
      handler() {
        this.syncFromProps();
      },
    },
    dates(newDates) {
      this.localDates = [...newDates];
    },
    periodOptions: {
      handler(options) {
        if (
          options.length &&
          !this.isValidPeriod(this.localState.month)
        ) {
          this.localState.month = options[0].value;
        }
      },
      immediate: true,
    },
  },
  methods: {
    syncFromProps() {
      this.localState = this.cloneState(this.filterState);
      this.localDates = [...this.dates];
    },
    isValidPeriod(month) {
      return this.periodOptions.some((option) => option.value === month);
    },
    getDefaultMonth() {
      return this.periodOptions.length
        ? this.periodOptions[0].value
        : "week";
    },
    cloneState(state) {
      const source = state || this.defaultFilterState;
      const month = this.isValidPeriod(source.month)
        ? source.month
        : this.getDefaultMonth();
      return {
        month,
        accounts: source.accounts ? [...source.accounts] : null,
        assets: source.assets ? [...source.assets] : null,
        sides: source.sides ? [...source.sides] : null,
        results: source.results ? [...source.results] : null,
        moods: source.moods ? [...source.moods] : null,
        strategies: source.strategies ? [...source.strategies] : null,
      };
    },
    close() {
      this.$emit("input", false);
    },
    selectPeriod(period) {
      this.localState.month = period;
      this.onPeriodChange(period);
    },
    onPeriodChange(period) {
      if (period !== "customPeriod") {
        this.localDates = [];
      }
    },
    isAllSelected(key, options) {
      const selected = this.localState[key];
      if (!selected || !selected.length) {
        return true;
      }
      return selected.length === options.length;
    },
    isOptionSelected(key, value, options) {
      const selected = this.localState[key];
      if (!selected || !selected.length) {
        return true;
      }
      return selected.includes(value);
    },
    toggleSelectAll(key, options) {
      const section = this.checkboxSections.find((s) => s.key === key);
      if (!section.multi) {
        this.localState[key] = null;
        return;
      }
      if (this.isAllSelected(key, options)) {
        this.localState[key] = [options[0].value];
      } else {
        this.localState[key] = null;
      }
    },
    toggleOption(key, value, options) {
      const section = this.checkboxSections.find((s) => s.key === key);
      if (section.multi) {
        let selected = this.localState[key];
        if (!selected || !selected.length) {
          selected = options.map((o) => o.value).filter((v) => v !== value);
        } else if (selected.includes(value)) {
          selected = selected.filter((v) => v !== value);
          if (!selected.length) {
            selected = null;
          }
        } else {
          selected = [...selected, value];
          if (selected.length === options.length) {
            selected = null;
          }
        }
        this.localState[key] = selected;
        return;
      }

      let selected = this.localState[key];
      if (!selected || !selected.length) {
        this.localState[key] = [value];
        return;
      }
      if (selected.includes(value) && selected.length === 1) {
        this.localState[key] = null;
        return;
      }
      this.localState[key] = [value];
    },
    apply() {
      if (this.isCustomPeriod && this.localDates.length !== 2) {
        return;
      }
      const sortedDates =
        this.isCustomPeriod && this.localDates.length === 2
          ? [...this.localDates].sort()
          : [...this.localDates];
      this.$emit("apply", {
        ...this.cloneState(this.localState),
        dates: sortedDates,
      });
    },
    clearAll() {
      this.$emit("clear");
    },
  },
};
</script>

<style scoped lang="scss">
.journal-filters-drawer {
  z-index: 9999 !important;

  ::v-deep {
    &.v-navigation-drawer {
      background-color: var(--v-mainWhite-base) !important;
    }

    .v-expansion-panels,
    .v-expansion-panel,
    .v-expansion-panel-header,
    .v-expansion-panel-content,
    .v-expansion-panel-content__wrap {
      background: transparent !important;
      background-color: transparent !important;
    }

    .v-expansion-panel-header--active,
    .v-expansion-panel-header.v-expansion-panel-header--active {
      background: transparent !important;
      background-color: transparent !important;
    }

    .v-expansion-panel::before {
      box-shadow: none !important;
    }

    .v-expansion-panel:not(:first-child)::after {
      border-color: var(--v-lightGray-base) !important;
    }
  }
}

.jfd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 12px;
  border-bottom: 1px solid var(--v-lightGray-base);
}

.jfd-title {
  font-family: "Avenir", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: var(--v-mainBlack-base);
}

.jfd-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--v-mediumGray-base);

  &:hover {
    background: var(--v-lightGray-base);
  }
}

.jfd-content {
  overflow-y: auto;
  max-height: calc(100vh - 140px);
  padding: 0 8px;
}

.jfd-panel-header {
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--v-mainBlack-base);
  min-height: 48px !important;

  ::v-deep .v-expansion-panel-header__icon .v-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 16px !important;
  }
}

.jfd-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 12px;
}

.jfd-period-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 12px;
}

.jfd-period-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: var(--v-lightGray-base);
  }

  &.active {
    background: var(--v-buttonSecondary-base);
  }
}

.jfd-period-marker {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1px solid #9a9a9a;
  background: var(--v-cardBackground-base);
  flex-shrink: 0;
  position: relative;
  transition: background 0.15s ease, border-color 0.15s ease;

  &.checked {
    background: var(--v-mainGreen-base);
    border-color: var(--v-mainGreen-base);

    &::after {
      content: "\f00c";
      font-family: "Font Awesome 6 Free", "FontAwesome";
      font-weight: 900;
      font-size: 11px;
      line-height: 1;
      color: #fff;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.jfd-period-label {
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  color: var(--v-mainBlack-base);
}

.jfd-date-picker {
  width: 100%;
}

.jfd-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px 24px;
}

.jfd-footer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.jfd-clear-btn,
.jfd-apply-btn {
  height: 44px !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  flex: 1;
  min-width: 0;
}

.jfd-apply-btn--compact {
  flex: 1;
}

.theme--dark {
  .journal-filters-drawer {
    ::v-deep {
      &.v-navigation-drawer {
        background-color: var(--v-mainWhite-base) !important;
      }

      .v-expansion-panels,
      .v-expansion-panel,
      .v-expansion-panel-header,
      .v-expansion-panel-content,
      .v-expansion-panel-content__wrap {
        background: transparent !important;
        background-color: transparent !important;
      }

      .v-expansion-panel-header--active,
      .v-expansion-panel-header.v-expansion-panel-header--active {
        background: transparent !important;
        background-color: transparent !important;
      }
    }

    .jfd-close-btn:hover,
    .jfd-period-option:hover {
      background: var(--v-buttonSecondaryHover-base);
    }

    .jfd-period-option.active {
      background: var(--v-buttonSecondary-base);
    }

    .jfd-period-marker {
      border-color: var(--v-mediumGray-base);
    }
  }
}
</style>
